import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { financeApi, arStatementApi, eventFinanceApi, formatError } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  Landmark, DollarSign, Plus, X, Loader2, Star, Package,
  Calendar, Lock, CheckCircle2, AlertTriangle, ChevronDown,
  ChevronUp, RefreshCw, Clock, History, Zap,
  Search, Filter, Shield, ShieldCheck, ShieldAlert, Activity,
  ChevronRight, Eye, Edit3, Hash, Database, GitBranch, Tag,
  FileText, AlertCircle, CheckCircle, TrendingUp, BarChart3,
  PlayCircle, Pause, ToggleLeft, ToggleRight, BookOpen,
  ClipboardList, Archive, Flag, Info, XCircle, Download,
  Send, GitCommit, Settings,
} from "lucide-react";
import EventFinanceMDMTab from "./EventFinanceMDMTab";

export default function FinanceSetup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("fiscal");
  const [currencies, setCurrencies] = useState([]);
  const [tenantCurrencies, setTenantCurrencies] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [profitCentres, setProfitCentres] = useState([]);
  const [approvalAuthorities, setApprovalAuthorities] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);

  // ERP-FIN-01A: Fiscal period state
  const [periodsData, setPeriodsData]       = useState(null);
  const [readiness, setReadiness]           = useState(null);
  const [fiscalLoading, setFiscalLoading]   = useState(false);
  const [actionLoading, setActionLoading]   = useState(null); // period_id of in-progress action
  const [historyPeriod, setHistoryPeriod]   = useState(null); // {period_id, calendar_id}
  const [history, setHistory]               = useState(null);
  const [reopenDialog, setReopenDialog]     = useState(null); // period record
  const [lockDialog, setLockDialog]         = useState(null); // period record
  const [reopenReason, setReopenReason]     = useState("");
  const [fyGenState, setFyGenState]         = useState(null); // {preview, mode, calendarId}
  const [fyStartYear, setFyStartYear]       = useState(2026); // default next FY
  const [showFyGen, setShowFyGen]           = useState(false);

  // ERP-FIN-02 AR-05: Accounting Readiness state
  const [ar05Data,      setAr05Data]      = useState(null);
  const [ar05Loading,   setAr05Loading]   = useState(false);

  useEffect(() => {
    Promise.all([
      financeApi.listCurrencies(), financeApi.listTenantCurrencies(),
      financeApi.listCostCentres(), financeApi.listProfitCentres(),
      financeApi.listApprovalAuthorities(), financeApi.listPaymentTerms(),
    ]).then(([cur, tc, cc, pc, aa, pt]) => {
      setCurrencies(cur.data); setTenantCurrencies(tc.data);
      setCostCentres(cc.data); setProfitCentres(pc.data);
      setApprovalAuthorities(aa.data); setPaymentTerms(pt.data);
    }).finally(() => setLoading(false));
  }, []);

  const loadFiscalData = useCallback(async () => {
    setFiscalLoading(true);
    try {
      const [pd, rd] = await Promise.all([
        financeApi.listFiscalPeriods(),
        financeApi.getFiscalReadiness(),
      ]);
      setPeriodsData(pd.data);
      setReadiness(rd.data);
    } catch (e) {
      toast.error("Failed to load fiscal periods");
    } finally {
      setFiscalLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "fiscal") loadFiscalData();
    if (tab === "ar_readiness" && !ar05Data) {
      setAr05Loading(true);
      arStatementApi.getAccountingReadiness()
        .then(r => setAr05Data(r.data))
        .catch(e => toast.error(formatError(e)))
        .finally(() => setAr05Loading(false));
    }
  }, [tab, loadFiscalData]);

  const loadHistory = async (calendarId, periodId) => {
    setHistoryPeriod({ calendarId, periodId });
    setHistory(null);
    try {
      const r = await financeApi.getPeriodHistory(calendarId, periodId);
      setHistory(r.data);
    } catch {
      setHistory({ error: true });
    }
  };

  const handlePeriodAction = async (period, newStatus) => {
    if (newStatus === "OPEN" && period.status === "CLOSED") {
      setReopenDialog(period);
      return;
    }
    if (newStatus === "LOCKED") {
      setLockDialog(period);
      return;
    }
    await executePeriodTransition(period, newStatus, null);
  };

  const executePeriodTransition = async (period, newStatus, reason) => {
    if (!periodsData) return;
    setActionLoading(period.period_id);
    try {
      await financeApi.updatePeriodStatus(periodsData.calendar_id, period.period_id, {
        status: newStatus,
        expected_version: period.period_version,
        reason: reason || null,
      });
      toast.success(`Period ${period.period_name} → ${newStatus}`);
      await loadFiscalData();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Transition failed";
      toast.error(msg);
    } finally {
      setActionLoading(null);
      setReopenDialog(null);
      setLockDialog(null);
      setReopenReason("");
    }
  };

  const handleGenerateFY = async (mode) => {
    if (!periodsData) return;
    try {
      const r = await financeApi.generateFiscalYear(periodsData.calendar_id, {
        fiscal_year_start: fyStartYear,
        mode,
      });
      if (mode === "PREVIEW") {
        setFyGenState({ preview: r.data, calendarId: periodsData.calendar_id });
      } else {
        toast.success(r.data.message || "FY periods generated");
        setShowFyGen(false);
        setFyGenState(null);
        await loadFiscalData();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    }
  };

  const reload = () => {
    Promise.all([
      financeApi.listTenantCurrencies(), financeApi.listCostCentres(),
      financeApi.listApprovalAuthorities(), financeApi.listPaymentTerms(),
    ]).then(([tc, cc, aa, pt]) => {
      setTenantCurrencies(tc.data); setCostCentres(cc.data);
      setApprovalAuthorities(aa.data); setPaymentTerms(pt.data);
    });
  };

  const TABS = [
    { id: "fiscal",       label: "Fiscal Periods",   count: periodsData?.total_periods ?? "—" },
    { id: "currency",     label: "Currency",          count: tenantCurrencies.length },
    { id: "cost",         label: "Cost Centres",      count: costCentres.length },
    { id: "aa",           label: "Approval Authorities", count: approvalAuthorities.length },
    { id: "pt",           label: "Payment Terms",     count: paymentTerms.length },
    { id: "coa",          label: "Chart of Accounts", count: "151" },
    { id: "ar_readiness", label: "AR-05 Readiness",   count: ar05Data ? (ar05Data.total_missing ?? 0) + " gaps" : "—" },
    { id: "ef_config",    label: "Event Finance Config", count: "6 types" },
  ];

  if (loading) return (
    <div className="p-6 animate-pulse space-y-4">
      <div className="h-8 bg-[#F3F3F3] rounded-lg w-64"/>
      <div className="h-12 bg-[#F3F3F3] rounded-xl"/>
      <div className="h-40 bg-[#F3F3F3] rounded-xl"/>
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#181818] font-sans">Finance Setup</h1>
        <p className="text-sm text-[#706E6B] mt-1">Fiscal periods, currencies, cost centres, approval authorities, payment terms</p>
      </div>

      {/* Quick links */}
      <div className="flex gap-3 flex-wrap">
        <button onClick={() => navigate("/finance/purchase-orders")}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          data-testid="goto-po-workspace-btn">
          <Package size={14} /> Purchase Orders
        </button>
        <button onClick={() => navigate("/finance/dashboard")}
          className="flex items-center gap-2 px-4 py-2.5 bg-zinc-100 border border-zinc-200 text-zinc-700 rounded-lg text-sm font-medium hover:bg-zinc-200 transition-colors">
          <Landmark size={14} /> Financial Dashboard
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-1 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30" : "text-[#706E6B] hover:text-[#181818]"
            }`}>
            {t.label}
            <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${tab === t.id ? "bg-[#EEF4FF] text-[#0176D3]" : "bg-[#E5E5E5] text-[#706E6B]"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* ── FISCAL PERIODS TAB ── */}
      {tab === "fiscal" && (
        <FiscalPeriodsTab
          periodsData={periodsData}
          readiness={readiness}
          loading={fiscalLoading}
          actionLoading={actionLoading}
          historyPeriod={historyPeriod}
          history={history}
          showFyGen={showFyGen}
          fyStartYear={fyStartYear}
          fyGenState={fyGenState}
          onRefresh={loadFiscalData}
          onAction={handlePeriodAction}
          onViewHistory={loadHistory}
          onCloseHistory={() => { setHistoryPeriod(null); setHistory(null); }}
          onShowFyGen={() => setShowFyGen(true)}
          onHideFyGen={() => { setShowFyGen(false); setFyGenState(null); }}
          onFyYearChange={setFyStartYear}
          onGenerateFY={handleGenerateFY}
        />
      )}

      {/* ── CURRENCY TAB ── */}
      {tab === "currency" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#444444] font-sans">Configured Currencies</h2>
            <button onClick={() => setShowForm("cur")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EEF4FF] border border-[#0176D3]/30 text-[#0176D3] text-xs font-medium hover:bg-[#E5E5E5]">
              <Plus className="w-3.5 h-3.5" /> Add Currency
            </button>
          </div>
          {tenantCurrencies.map(tc => (
            <div key={tc.id} className="flex items-center justify-between p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl hover:border-[#0176D3]/30 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#EEF4FF] flex items-center justify-center">
                  <span className="text-sm font-bold text-[#0176D3] font-mono">{tc.currency_code}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#181818]">{tc.currency_code}</p>
                  <p className="text-xs text-[#706E6B]">{tc.currency_info?.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {tc.is_base_currency && (
                  <span className="flex items-center gap-1 text-xs font-mono text-[#8C4B00] bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded">
                    <Star className="w-3 h-3" /> Base
                  </span>
                )}
                <StatusBadge status={tc.is_active ? "ACTIVE" : "NOT_STARTED"} />
              </div>
            </div>
          ))}
          {tenantCurrencies.length === 0 && <Empty msg="No currencies configured" />}
        </div>
      )}

      {tab === "cost" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#444444] font-sans">Cost Centres</h2>
            <button onClick={() => setShowForm("cc")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EEF4FF] border border-[#0176D3]/30 text-[#0176D3] text-xs font-medium hover:bg-[#E5E5E5]">
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          {costCentres.map(cc => <FinanceRow key={cc.id} title={cc.name} code={cc.code} status={cc.status} />)}
          {costCentres.length === 0 && <Empty msg="No cost centres configured" />}
        </div>
      )}

      {tab === "aa" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#444444] font-sans">Approval Authorities</h2>
            <button onClick={() => setShowForm("aa")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EEF4FF] border border-[#0176D3]/30 text-[#0176D3] text-xs font-medium hover:bg-[#E5E5E5]">
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          {approvalAuthorities.map(aa => (
            <div key={aa.id} className="flex items-center justify-between p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl">
              <div>
                <p className="text-sm font-semibold text-[#181818]">{aa.name}</p>
                <p className="text-xs font-mono text-[#706E6B] mt-0.5">{aa.code} · Role: {aa.role}</p>
                {aa.approval_limit && <p className="text-xs text-[#706E6B] mt-0.5">Limit: {aa.currency_code} {aa.approval_limit?.toLocaleString()}</p>}
              </div>
              <StatusBadge status={aa.status} />
            </div>
          ))}
          {approvalAuthorities.length === 0 && <Empty msg="No approval authorities configured" />}
        </div>
      )}

      {tab === "pt" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#444444] font-sans">Payment Terms</h2>
            <button onClick={() => setShowForm("pt")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EEF4FF] border border-[#0176D3]/30 text-[#0176D3] text-xs font-medium hover:bg-[#E5E5E5]">
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          {paymentTerms.map(pt => <FinanceRow key={pt.id} title={pt.name} code={`${pt.code} · ${pt.days} days`} status={pt.status} />)}
          {paymentTerms.length === 0 && <Empty msg="No payment terms configured" />}
        </div>
      )}

      {/* ── CHART OF ACCOUNTS TAB ── */}
      {tab === "coa" && <ChartOfAccountsTab />}

      {/* ── AR-05 ACCOUNTING READINESS TAB ── */}
      {tab === "ar_readiness" && (
        <AR05ReadinessTab
          data={ar05Data}
          loading={ar05Loading}
          onRefresh={() => {
            setAr05Data(null);
            setAr05Loading(true);
            arStatementApi.getAccountingReadiness()
              .then(r => setAr05Data(r.data))
              .catch(e => toast.error(formatError(e)))
              .finally(() => setAr05Loading(false));
          }}
        />
      )}

      {/* ── EFIN-04 EVENT FINANCE CONFIG TAB ── */}
      {tab === "ef_config" && <EventFinanceMDMTab />}

      {/* ── Modals ── */}
      {showForm === "cur" && (
        <Modal title="Add Currency" onClose={() => setShowForm(null)}>
          <CurrencyForm currencies={currencies} onSuccess={() => { setShowForm(null); financeApi.listTenantCurrencies().then(r => setTenantCurrencies(r.data)); }} />
        </Modal>
      )}
      {showForm === "cc" && (
        <Modal title="Add Cost Centre" onClose={() => setShowForm(null)}>
          <SimpleForm fields={[["code","Code","CC-001",true],["name","Name","Events Operations",true]]}
            onSubmit={async (data) => { await financeApi.createCostCentre(data); toast.success("Cost Centre created"); setShowForm(null); financeApi.listCostCentres().then(r => setCostCentres(r.data)); }} />
        </Modal>
      )}
      {showForm === "aa" && (
        <Modal title="Add Approval Authority" onClose={() => setShowForm(null)}>
          <ApprovalAuthorityForm onSuccess={() => { setShowForm(null); financeApi.listApprovalAuthorities().then(r => setApprovalAuthorities(r.data)); }} />
        </Modal>
      )}
      {showForm === "pt" && (
        <Modal title="Add Payment Term" onClose={() => setShowForm(null)}>
          <SimpleForm fields={[["code","Code","NET-30",true],["name","Name","Net 30 Days",true],["days","Days","30",true,"number"]]}
            onSubmit={async (data) => { await financeApi.createPaymentTerm({...data, days: parseInt(data.days)}); toast.success("Payment Term created"); setShowForm(null); financeApi.listPaymentTerms().then(r => setPaymentTerms(r.data)); }} />
        </Modal>
      )}

      {/* Reopen dialog */}
      {reopenDialog && (
        <Modal title={`Reopen Period — ${reopenDialog.period_name}`} onClose={() => { setReopenDialog(null); setReopenReason(""); }}>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
              Reopening a CLOSED period allows new financial postings against it. A justification is required for audit purposes.
            </div>
            <div>
              <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">Reason *</label>
              <textarea value={reopenReason} onChange={e => setReopenReason(e.target.value)} rows={3}
                placeholder="e.g. Pending supplier invoices require posting before quarter close"
                className="w-full px-3 py-2 rounded-lg bg-white border border-[#E5E5E5] text-[#181818] text-sm placeholder:text-[#706E6B] focus:outline-none focus:border-[#0176D3] transition-colors resize-none" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setReopenDialog(null); setReopenReason(""); }}
                className="flex-1 py-2 rounded-lg border border-[#E5E5E5] text-[#706E6B] text-sm hover:bg-[#F3F3F3] transition-colors">
                Cancel
              </button>
              <button
                disabled={!reopenReason.trim() || !!actionLoading}
                onClick={() => executePeriodTransition(reopenDialog, "OPEN", reopenReason)}
                data-testid="confirm-reopen-btn"
                className="flex-1 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-40">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Reopen Period"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Lock confirmation dialog */}
      {lockDialog && (
        <Modal title={`Lock Period — ${lockDialog.period_name}`} onClose={() => setLockDialog(null)}>
          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
              <strong>This action is permanent and irreversible.</strong> Locking a period permanently seals it — no further postings or reopening will be permitted, even by administrators.
            </div>
            <div className="flex gap-2">
              <button onClick={() => setLockDialog(null)}
                className="flex-1 py-2 rounded-lg border border-[#E5E5E5] text-[#706E6B] text-sm hover:bg-[#F3F3F3] transition-colors">
                Cancel
              </button>
              <button
                disabled={!!actionLoading}
                onClick={() => executePeriodTransition(lockDialog, "LOCKED", null)}
                data-testid="confirm-lock-btn"
                className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-40">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Lock Period Permanently"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Fiscal Periods Tab Component ──────────────────────────────────────────────

function FiscalPeriodsTab({
  periodsData, readiness, loading, actionLoading, historyPeriod, history,
  showFyGen, fyStartYear, fyGenState,
  onRefresh, onAction, onViewHistory, onCloseHistory, onShowFyGen, onHideFyGen,
  onFyYearChange, onGenerateFY,
}) {
  if (loading && !periodsData) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-24 bg-[#F3F3F3] rounded-xl"/>
        <div className="h-12 bg-[#F3F3F3] rounded-xl"/>
        {[...Array(6)].map((_, i) => <div key={i} className="h-16 bg-[#F3F3F3] rounded-xl"/>)}
      </div>
    );
  }

  if (!periodsData) {
    return <div className="text-center py-12 text-sm text-[#706E6B]">No fiscal calendar found. Contact your Finance team.</div>;
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-5">
      {/* Readiness banner */}
      {readiness && <ReadinessBanner readiness={readiness} />}

      {/* Calendar header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-semibold text-[#181818]" data-testid="calendar-name">
            {periodsData.calendar_name}
          </h2>
          <p className="text-xs text-[#706E6B] mt-0.5">
            {periodsData.total_periods} periods · {periodsData.open_count} open · {periodsData.locked_count} locked
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onRefresh} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E5E5E5] text-[#706E6B] text-xs hover:bg-[#F3F3F3] transition-colors"
            data-testid="refresh-periods-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button onClick={onShowFyGen}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EEF4FF] border border-[#0176D3]/30 text-[#0176D3] text-xs font-medium hover:bg-[#E5E5E5]"
            data-testid="generate-fy-btn">
            <Zap className="w-3.5 h-3.5" /> Generate FY
          </button>
        </div>
      </div>

      {/* FY Generator */}
      {showFyGen && (
        <FYGenerator
          fyStartYear={fyStartYear}
          fyGenState={fyGenState}
          onYearChange={onFyYearChange}
          onPreview={() => onGenerateFY("PREVIEW")}
          onApply={() => onGenerateFY("APPLY")}
          onClose={onHideFyGen}
        />
      )}

      {/* Period grid */}
      <div className="space-y-2" data-testid="period-list">
        {periodsData.periods.map(period => (
          <PeriodRow
            key={period.period_id}
            period={period}
            today={today}
            actionLoading={actionLoading}
            historyPeriod={historyPeriod}
            history={history}
            calendarId={periodsData.calendar_id}
            onAction={onAction}
            onViewHistory={onViewHistory}
            onCloseHistory={onCloseHistory}
          />
        ))}
      </div>

      {periodsData.periods.length === 0 && (
        <Empty msg="No periods configured for this calendar. Use Generate FY to create periods." />
      )}
    </div>
  );
}

function ReadinessBanner({ readiness }) {
  const { status, checks, today } = readiness;
  const icon = status === "READY"
    ? <CheckCircle2 className="w-4 h-4 text-green-600" />
    : <AlertTriangle className="w-4 h-4 text-amber-500" />;
  const bg = status === "READY" ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200";
  const text = status === "READY" ? "text-green-800" : "text-amber-800";

  return (
    <div className={`p-4 rounded-xl border ${bg} space-y-3`} data-testid="readiness-banner">
      <div className="flex items-center gap-2">
        {icon}
        <span className={`text-sm font-semibold ${text}`}>
          {status === "READY" ? "Finance configuration ready" : "Finance configuration incomplete"}
        </span>
        <span className="ml-auto text-xs text-[#706E6B]">{today}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {checks.map(c => (
          <div key={c.check} className="flex items-center gap-1.5 text-xs">
            <span className={c.ok ? "text-green-500" : "text-amber-500"}>
              {c.ok ? "✓" : "!"}
            </span>
            <span className={c.ok ? "text-[#444444]" : "text-amber-700"}>{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PeriodRow({ period, today, actionLoading, historyPeriod, history, calendarId, onAction, onViewHistory, onCloseHistory }) {
  const isCurrent = period.start_date <= today && today <= period.end_date;
  const isHistoryOpen = historyPeriod?.periodId === period.period_id;

  const STATUS_STYLES = {
    FUTURE:  "bg-zinc-100 text-zinc-500 border-zinc-200",
    OPEN:    "bg-green-50 text-green-700 border-green-200",
    CLOSED:  "bg-amber-50 text-amber-700 border-amber-200",
    LOCKED:  "bg-red-50 text-red-700 border-red-200",
  };
  const badge = STATUS_STYLES[period.status] || STATUS_STYLES.FUTURE;
  const busy = actionLoading === period.period_id;

  return (
    <div className={`rounded-xl border transition-colors ${
      isCurrent ? "border-[#0176D3]/40 bg-[#EEF4FF]/30" : "border-[#E5E5E5] bg-white"
    }`} data-testid={`period-row-${period.period_id}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Current indicator */}
        {isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-[#0176D3] shrink-0" />}
        {!isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-transparent shrink-0" />}

        {/* Period info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-[#181818]">{period.period_name}</span>
            {isCurrent && <span className="text-xs text-[#0176D3] font-mono">current</span>}
          </div>
          <span className="text-xs text-[#706E6B] font-mono">{period.start_date} – {period.end_date}</span>
        </div>

        {/* Status badge */}
        <span className={`text-xs font-mono px-2 py-0.5 rounded border ${badge} shrink-0`}
          data-testid={`period-status-${period.period_id}`}>
          {period.status}
        </span>

        {/* Version */}
        <span className="text-xs text-[#706E6B] font-mono shrink-0">v{period.period_version}</span>

        {/* Action buttons */}
        <div className="flex items-center gap-1 shrink-0">
          {period.status === "FUTURE" && (
            <ActionBtn onClick={() => onAction(period, "OPEN")} busy={busy}
              label="Open" className="bg-green-600 text-white hover:bg-green-700"
              testId={`open-btn-${period.period_id}`} />
          )}
          {period.status === "OPEN" && (
            <ActionBtn onClick={() => onAction(period, "CLOSED")} busy={busy}
              label="Close" className="bg-amber-500 text-white hover:bg-amber-600"
              testId={`close-btn-${period.period_id}`} />
          )}
          {period.status === "CLOSED" && (
            <>
              <ActionBtn onClick={() => onAction(period, "OPEN")} busy={busy}
                label="Reopen" className="bg-[#0176D3] text-white hover:bg-blue-700"
                testId={`reopen-btn-${period.period_id}`} />
              <ActionBtn onClick={() => onAction(period, "LOCKED")} busy={busy}
                label="Lock" className="bg-red-600 text-white hover:bg-red-700"
                testId={`lock-btn-${period.period_id}`}
                icon={<Lock className="w-3 h-3" />} />
            </>
          )}
          {period.status === "LOCKED" && (
            <span className="flex items-center gap-1 text-xs text-red-600 font-mono px-2 py-1">
              <Lock className="w-3 h-3" /> terminal
            </span>
          )}

          {/* History toggle */}
          <button onClick={() => isHistoryOpen ? onCloseHistory() : onViewHistory(calendarId, period.period_id)}
            className="p-1.5 rounded text-[#706E6B] hover:text-[#181818] hover:bg-[#F3F3F3] transition-colors"
            data-testid={`history-btn-${period.period_id}`}
            title="View transition history">
            {isHistoryOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <History className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* History drawer */}
      {isHistoryOpen && (
        <div className="border-t border-[#E5E5E5] px-4 py-3 bg-[#F9F9F9] rounded-b-xl">
          {!history && <div className="flex items-center gap-2 text-xs text-[#706E6B]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading history…</div>}
          {history?.error && <div className="text-xs text-red-500">Failed to load history.</div>}
          {history && !history.error && (
            history.event_count === 0
              ? <p className="text-xs text-[#706E6B]">No transitions recorded yet.</p>
              : <div className="space-y-2">
                  {history.history.map((ev, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <span className="font-mono text-[#706E6B] shrink-0">{(ev.timestamp||"").slice(0,16)}</span>
                      <span className="text-[#444444]">
                        <span className="font-medium">{ev.old_status} → {ev.new_status}</span>
                        {ev.reason && <span className="text-[#706E6B]"> · "{ev.reason}"</span>}
                        <span className="text-[#706E6B]"> by {ev.actor_email}</span>
                      </span>
                    </div>
                  ))}
                </div>
          )}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ onClick, busy, label, className, testId, icon }) {
  return (
    <button onClick={onClick} disabled={busy}
      className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded transition-colors disabled:opacity-40 ${className}`}
      data-testid={testId}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : icon}
      {!busy && label}
    </button>
  );
}

function FYGenerator({ fyStartYear, fyGenState, onYearChange, onPreview, onApply, onClose }) {
  const fyEnd = fyStartYear + 1;
  const fyLabel = `FY ${fyStartYear}–${fyEnd}`;
  const preview = fyGenState?.preview;

  return (
    <div className="border border-[#E5E5E5] rounded-xl bg-white p-4 space-y-4" data-testid="fy-generator">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#181818]">Generate Fiscal Year Periods</h3>
        <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">FY Start Year</label>
          <select value={fyStartYear} onChange={e => onYearChange(parseInt(e.target.value))}
            className="px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm focus:outline-none focus:border-[#0176D3]">
            {[2025, 2026, 2027, 2028].map(y => (
              <option key={y} value={y}>{`FY ${y}–${y+1}`}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onPreview}
            className="px-4 py-2 rounded-lg border border-[#0176D3]/30 bg-[#EEF4FF] text-[#0176D3] text-sm font-medium hover:bg-[#E5E5E5]"
            data-testid="preview-fy-btn">
            Preview {fyLabel}
          </button>
          {preview && (
            <button onClick={onApply} disabled={preview.overlap_errors?.length > 0}
              className="px-4 py-2 rounded-lg bg-[#0176D3] text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40"
              data-testid="apply-fy-btn">
              Apply ({preview.to_create_count} new periods)
            </button>
          )}
        </div>
      </div>

      {preview && (
        <div className="space-y-2">
          {preview.overlap_errors?.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 space-y-1">
              <p className="font-semibold">Overlap conflicts:</p>
              {preview.overlap_errors.map((e, i) => <p key={i}>· {e}</p>)}
            </div>
          )}
          {preview.periods_skipped?.length > 0 && (
            <p className="text-xs text-[#706E6B]">Already exist (skipped): {preview.periods_skipped.join(", ")}</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {preview.periods_preview.map(p => (
              <div key={p.period_id} className="text-xs p-2 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg">
                <p className="font-medium text-[#181818]">{p.period_name}</p>
                <p className="text-[#706E6B] font-mono">{p.start_date}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared form components ────────────────────────────────────────────────────

function CurrencyForm({ currencies, onSuccess }) {
  const [code, setCode] = useState("");
  const [isBase, setIsBase] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await financeApi.addTenantCurrency({ currency_code: code, is_base_currency: isBase }); toast.success(`Currency ${code} added`); onSuccess(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <SelectField label="Currency" value={code} onChange={setCode} options={[["","Select currency"],...currencies.map(c=>[c.code,`${c.code} - ${c.name}`])]} />
      <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5]">
        <input type="checkbox" id="isBase" checked={isBase} onChange={e=>setIsBase(e.target.checked)} className="w-4 h-4 accent-[#0176D3]" />
        <label htmlFor="isBase" className="text-sm text-[#444444]">Set as base currency</label>
      </div>
      <SubmitBtn loading={loading} label="Add Currency" />
    </form>
  );
}

function ApprovalAuthorityForm({ onSuccess }) {
  const [form, setForm] = useState({ code: "", name: "", role: "FINANCE_USER", approval_limit: "", currency_code: "INR" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await financeApi.createApprovalAuthority({...form, approval_limit: form.approval_limit ? parseFloat(form.approval_limit) : null}); toast.success("Created"); onSuccess(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <FormField label="Code" value={form.code} onChange={v=>setForm({...form,code:v})} placeholder="AA-001" required />
      <FormField label="Name" value={form.name} onChange={v=>setForm({...form,name:v})} placeholder="Finance Controller" required />
      <SelectField label="Role" value={form.role} onChange={v=>setForm({...form,role:v})} options={[["FINANCE_USER","Finance User"],["TENANT_ADMIN","Tenant Admin"],["OPERATIONS_USER","Operations User"]]} />
      <FormField label="Approval Limit (optional)" value={form.approval_limit} onChange={v=>setForm({...form,approval_limit:v})} placeholder="1000000" type="number" />
      <SubmitBtn loading={loading} label="Create Approval Authority" />
    </form>
  );
}

function SimpleForm({ fields, onSubmit }) {
  const [form, setForm] = useState(Object.fromEntries(fields.map(([k]) => [k, ""])));
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await onSubmit(form); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      {fields.map(([k,l,p,req,type]) => (
        <FormField key={k} label={l} value={form[k]} onChange={v=>setForm({...form,[k]:v})} placeholder={p} required={req} type={type||"text"} />
      ))}
      <SubmitBtn loading={loading} label="Create" />
    </form>
  );
}

function FinanceRow({ title, code, status }) {
  return (
    <div className="flex items-center justify-between p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl hover:border-[#0176D3]/30 transition-colors">
      <div>
        <p className="text-sm font-semibold text-[#181818]">{title}</p>
        <p className="text-xs font-mono text-[#706E6B] mt-0.5">{code}</p>
      </div>
      <StatusBadge status={status} />
    </div>
  );
}

function Empty({ msg }) {
  return <div className="text-center py-8 text-sm text-[#706E6B] bg-[#F3F3F3] border border-dashed border-[#E5E5E5] rounded-xl">{msg}</div>;
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-[#181818] font-sans">{title}</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, required, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{label}{required && " *"}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required}
        className="w-full px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm placeholder:text-[#706E6B] focus:outline-none focus:border-[#0176D3] transition-colors" />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm focus:outline-none focus:border-[#0176D3] transition-colors">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function SubmitBtn({ loading, label }) {
  return (
    <button type="submit" disabled={loading}
      className="w-full py-2.5 rounded-lg bg-[#0176D3] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 mt-2">
      {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : label}
    </button>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// CHART OF ACCOUNTS TAB — ERP-FIN-01B Gate 2D
// All 15 requirements: version selector, hierarchy, search/filter, details,
// add/edit/toggle, conflict review, deferred panel, readiness dashboard,
// import migration, request activation, audit history
// ═══════════════════════════════════════════════════════════════════════════

const CLS_COLOR = {
  ASSET:     "bg-sky-100 text-sky-700",
  LIABILITY: "bg-orange-100 text-orange-700",
  EQUITY:    "bg-violet-100 text-violet-700",
  REVENUE:   "bg-emerald-100 text-emerald-700",
  EXPENSE:   "bg-red-100 text-red-700",
};

function ClassBadge({ cls }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-mono font-semibold uppercase ${CLS_COLOR[cls] || "bg-gray-100 text-gray-600"}`}>
      {cls || "—"}
    </span>
  );
}

function VersionBadge({ status }) {
  const cfg = {
    ACTIVE:  { bg: "bg-emerald-50 border-emerald-300 text-emerald-700", icon: <CheckCircle className="w-3 h-3"/> },
    DRAFT:   { bg: "bg-amber-50 border-amber-300 text-amber-700", icon: <GitCommit className="w-3 h-3"/> },
    ARCHIVED:{ bg: "bg-gray-100 border-gray-300 text-gray-600", icon: <Archive className="w-3 h-3"/> },
  };
  const { bg, icon } = cfg[status] || cfg.ARCHIVED;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-mono font-semibold border px-2 py-0.5 rounded ${bg}`}>
      {icon}{status}
    </span>
  );
}

function COAStatCard({ label, value, sub, variant = "neutral" }) {
  const ring = { neutral:"border-[#E5E5E5]", warn:"border-amber-300 bg-amber-50", ok:"border-emerald-300 bg-emerald-50", info:"border-sky-300 bg-sky-50" };
  return (
    <div className={`rounded-xl border p-4 ${ring[variant]}`}>
      <p className="text-xs text-[#706E6B] font-mono uppercase tracking-widest mb-1">{label}</p>
      <p className="text-2xl font-bold text-[#181818] font-mono">{value ?? "—"}</p>
      {sub && <p className="text-xs text-[#706E6B] mt-0.5">{sub}</p>}
    </div>
  );
}

function COAAccountRow({ val, selected, onSelect }) {
  return (
    <tr
      onClick={() => onSelect(val)}
      data-testid={`coa-row-${val.code}`}
      className={`cursor-pointer border-b border-[#E5E5E5] text-sm transition-colors ${selected ? "bg-[#EEF4FF]" : "hover:bg-[#F9F9F9]"}`}
    >
      <td className="px-3 py-2 font-mono text-[#181818] font-semibold w-20">{val.code}</td>
      <td className="px-3 py-2 text-[#444444] max-w-xs">
        <div className="flex items-center gap-1.5">
          {val.name}
          {val.conflict_flag && <Flag className="w-3 h-3 text-amber-500 flex-shrink-0" title={`Conflict: ${val.conflict_flag}`} />}
          {val.defer_flag && <Archive className="w-3 h-3 text-gray-400 flex-shrink-0" title={`Deferred: ${val.defer_flag}`} />}
        </div>
      </td>
      <td className="px-3 py-2"><ClassBadge cls={val.account_classification} /></td>
      <td className="px-3 py-2">
        <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${val.enabled ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
          {val.enabled ? "Enabled" : "Disabled"}
        </span>
      </td>
    </tr>
  );
}

function ChartOfAccountsTab() {
  // ── Data state ──────────────────────────────────────────────────────────
  const [coaList, setCoaList]               = useState([]);
  const [versions, setVersions]             = useState([]);
  const [selectedVid, setSelectedVid]       = useState(null);
  const [segments, setSegments]             = useState([]);
  const [nacctSeg, setNacctSeg]             = useState(null);
  const [values, setValues]                 = useState([]);
  const [readiness, setReadiness]           = useState(null);
  const [migStatus, setMigStatus]           = useState(null);
  const [migResult, setMigResult]           = useState(null);
  const [rulesResult, setRulesResult]       = useState(null);
  const [activationReqs, setActivationReqs] = useState([]);
  const [rules, setRules]                   = useState([]);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [loading, setLoading]               = useState(false);
  const [migRunning, setMigRunning]         = useState(false);
  const [rulesRunning, setRulesRunning]     = useState(false);
  const [selectedVal, setSelectedVal]       = useState(null);
  const [editForm, setEditForm]             = useState(null);
  const [addForm, setAddForm]               = useState(false);
  const [activationModal, setActivationModal] = useState(false);
  const [subPanel, setSubPanel]             = useState("list"); // list|conflicts|deferred|migration|rules|activation
  const [search, setSearch]                 = useState("");
  const [filterCls, setFilterCls]           = useState("");
  const [filterEnabled, setFilterEnabled]   = useState("");
  const [showConflict, setShowConflict]     = useState(false);
  const [showDeferred, setShowDeferred]     = useState(false);
  const [activationForm, setActivationForm] = useState({ justification: "", requested_effective_date: "2026-04-01", acknowledgements: [] });
  const [addAccountForm, setAddAccountForm] = useState({ code: "", name: "", account_classification: "ASSET", account_subtype: "", effective_from: "2026-01-01" });

  // ── Derived ───────────────────────────────────────────────────────────────
  const selectedVersion = versions.find(v => v.id === selectedVid);
  const isDraft = selectedVersion?.status === "DRAFT";
  const isActive = selectedVersion?.status === "ACTIVE";

  const filteredValues = values.filter(v => {
    if (search && !v.code?.toLowerCase().includes(search.toLowerCase()) && !v.name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCls && v.account_classification !== filterCls) return false;
    if (filterEnabled === "enabled" && !v.enabled) return false;
    if (filterEnabled === "disabled" && v.enabled) return false;
    if (showConflict && !v.conflict_flag) return false;
    if (showDeferred && !v.defer_flag) return false;
    return true;
  });

  const conflictValues = values.filter(v => v.conflict_flag);
  const deferredValues = values.filter(v => v.defer_flag);

  // ── Data loading ──────────────────────────────────────────────────────────
  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [coaRes, statusRes] = await Promise.all([
        financeApi.listCOA(),
        financeApi.getMigrationStatus(),
      ]);
      setCoaList(coaRes.data || []);
      setMigStatus(statusRes.data);

      if ((coaRes.data || []).length > 0) {
        const coaId = coaRes.data[0].id;
        await loadVersions(coaId);
      }

      // Load v2 accounting rules
      try {
        const rRes = await financeApi.listV2AccountingRules();
        setRules(rRes.data || []);
      } catch {}
    } catch (e) {
      toast.error("Failed to load COA data: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }

  async function loadVersions(coaId) {
    const vRes = await financeApi.listCOAVersions(coaId);
    const vList = vRes.data || [];
    setVersions(vList);
    // Default: prefer v2 DRAFT, else v1 ACTIVE
    const v2 = vList.find(v => v.status === "DRAFT");
    const v1 = vList.find(v => v.status === "ACTIVE");
    const def = v2 || v1;
    if (def) {
      setSelectedVid(def.id);
      await loadVersionData(def.id);
    }
  }

  async function loadVersionData(vid) {
    try {
      const [segRes, readRes, reqRes] = await Promise.all([
        financeApi.listSegments(vid),
        financeApi.getVersionReadiness(vid),
        financeApi.getActivationRequests(vid),
      ]);
      const segs = segRes.data || [];
      setSegments(segs);
      setReadiness(readRes.data);
      setActivationReqs(reqRes.data || []);
      const nacct = segs.find(s => s.segment_type === "NATURAL_ACCOUNT");
      setNacctSeg(nacct);
      if (nacct) {
        const valRes = await financeApi.listValues(nacct.id);
        setValues(valRes.data || []);
      } else {
        setValues([]);
      }
    } catch (e) {
      // Version data might not exist if v2 not seeded yet
      setSegments([]); setReadiness(null); setActivationReqs([]);
      setNacctSeg(null); setValues([]);
    }
  }

  async function handleVersionChange(vid) {
    setSelectedVid(vid);
    setSelectedVal(null);
    setValues([]);
    await loadVersionData(vid);
  }

  async function handleToggleEnabled(val) {
    if (!isDraft) return;
    try {
      await financeApi.updateValue(val.id, { enabled: !val.enabled });
      toast.success(`Account ${val.code} ${!val.enabled ? "enabled" : "disabled"}`);
      const valRes = await financeApi.listValues(nacctSeg.id);
      setValues(valRes.data || []);
      if (selectedVal?.id === val.id) setSelectedVal(prev => ({ ...prev, enabled: !prev.enabled }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  }

  async function handleEditSubmit(e) {
    e.preventDefault();
    try {
      await financeApi.updateValue(editForm.id, { name: editForm.name, description: editForm.description, account_subtype: editForm.account_subtype });
      toast.success("Account updated");
      setEditForm(null);
      const valRes = await financeApi.listValues(nacctSeg.id);
      setValues(valRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  }

  async function handleAddAccount(e) {
    e.preventDefault();
    try {
      await financeApi.createValue(nacctSeg.id, { ...addAccountForm, posting_allowed: true, enabled: true });
      toast.success("Account added to DRAFT version");
      setAddForm(false);
      setAddAccountForm({ code: "", name: "", account_classification: "ASSET", account_subtype: "", effective_from: "2026-01-01" });
      const valRes = await financeApi.listValues(nacctSeg.id);
      setValues(valRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  }

  async function runMigration(dryRun) {
    setMigRunning(true);
    try {
      const res = await financeApi.runMigration(dryRun);
      setMigResult(res.data);
      if (!dryRun) {
        toast.success(`Migration applied — ${res.data.summary.created} created, ${res.data.summary.skipped} skipped`);
        await loadAll();
      } else {
        toast.info("Dry-run complete — review results, then click Apply");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Migration failed");
    } finally {
      setMigRunning(false);
    }
  }

  async function runRulesMigration(dryRun) {
    setRulesRunning(true);
    try {
      const res = await financeApi.runAccountingRulesMigration(dryRun);
      setRulesResult(res.data);
      if (!dryRun) {
        toast.success(`Rules seeded — ${res.data.summary.created} created`);
        const rRes = await financeApi.listV2AccountingRules();
        setRules(rRes.data || []);
      } else {
        toast.info("Rules dry-run complete");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rules migration failed");
    } finally {
      setRulesRunning(false);
    }
  }

  async function handleRequestActivation(e) {
    e.preventDefault();
    try {
      await financeApi.requestActivation(selectedVid, activationForm);
      toast.success("Activation request submitted — PENDING Finance Owner review");
      setActivationModal(false);
      setActivationForm({ justification: "", requested_effective_date: "2026-04-01", acknowledgements: [] });
      await loadVersionData(selectedVid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    }
  }

  const pendingReq = activationReqs.find(r => r.status === "PENDING_REVIEW");

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="space-y-3 animate-pulse">
      {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-[#F3F3F3] rounded-xl" />)}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="coa-tab">
      {/* ── Version Selector ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-[#0176D3]" />
          <h2 className="text-base font-semibold text-[#181818]">Chart of Accounts</h2>
          {selectedVersion && <VersionBadge status={selectedVersion.status} />}
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="coa-version-selector"
            value={selectedVid || ""}
            onChange={e => handleVersionChange(e.target.value)}
            className="text-sm px-3 py-1.5 rounded-lg border border-[#E5E5E5] bg-white text-[#181818] focus:outline-none focus:border-[#0176D3]"
          >
            {versions.map(v => (
              <option key={v.id} value={v.id}>
                {v.description || `v${v.version_number}`} — {v.status}
              </option>
            ))}
            {versions.length === 0 && <option value="">No versions (run migration)</option>}
          </select>
          <button onClick={loadAll} className="p-1.5 rounded-lg border border-[#E5E5E5] text-[#706E6B] hover:text-[#181818] hover:bg-[#F3F3F3] transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── V2 DRAFT Guard Banner ── */}
      {isDraft && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-300 rounded-xl" data-testid="coa-draft-guard">
          <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">COA v2 — DRAFT (Not Authorised for Activation)</p>
            <p className="text-xs text-amber-700 mt-0.5">
              This version is in controlled staging. Editing is permitted for Finance Administrators. Activation requires Finance Owner sign-off per ERP-FIN-01B-GATE-2B.md. COA v1 (ACTIVE) is fully preserved.
            </p>
          </div>
        </div>
      )}

      {/* ── Version Readiness Dashboard ── */}
      {readiness && (
        <div data-testid="coa-readiness-dashboard" className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <COAStatCard label="Total Accounts" value={readiness.total_values ?? values.length} variant="neutral" />
            <COAStatCard label="Enabled" value={readiness.posting_values ?? values.filter(v => v.enabled).length} variant="ok" />
            <COAStatCard label="Deferred (R2)" value={deferredValues.length || readiness.deferred_values} variant="warn" sub="Deferred to Phase 3" />
            <COAStatCard label="Conflict Accounts" value={conflictValues.length || readiness.conflict_values} variant="info" sub="ADD_DISTINCT (Gate 2B)" />
          </div>
          {isDraft && (
            <div className="flex items-center gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-lg">
              <ShieldCheck className="w-4 h-4 text-zinc-500" />
              <p className="text-xs text-zinc-600">
                <span className="font-semibold">Readiness:</span> v1 preserved ({migStatus?.v1_accounts_preserved || 9} accounts) ·
                v2 seeded: <span className={`font-semibold ${migStatus?.v2_seeded_complete ? "text-emerald-700" : "text-amber-700"}`}>{migStatus?.v2_seeded_complete ? "COMPLETE" : "PENDING"}</span> ·
                Draft rules: {migStatus?.draft_rules_seeded || 0} ·
                Checksum: <span className="font-mono text-xs">{(migStatus?.manifest_checksum || "").slice(0, 12)}...</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Not seeded yet state ── */}
      {!nacctSeg && !loading && (
        <div className="p-8 text-center border-2 border-dashed border-[#E5E5E5] rounded-xl">
          <Database className="w-10 h-10 text-[#706E6B] mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#444444] mb-1">COA v2 DRAFT has not been seeded yet</p>
          <p className="text-xs text-[#706E6B] mb-4">Open the "Migration" panel to run the Gate 2C dry-preview then apply</p>
          <button
            onClick={() => setSubPanel("migration")}
            className="px-4 py-2 bg-[#0176D3] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
            data-testid="open-migration-panel-btn"
          >
            Open Migration Panel
          </button>
        </div>
      )}

      {/* ── Sub-panel Tabs ── */}
      <div className="flex gap-1 flex-wrap border-b border-[#E5E5E5] pb-0">
        {[
          { id: "list",       label: `Accounts (${filteredValues.length})`, testId: "coa-subpanel-accounts" },
          { id: "conflicts",  label: `Conflicts (${conflictValues.length})`, testId: "coa-subpanel-conflicts" },
          { id: "deferred",   label: `Deferred (${deferredValues.length})`, testId: "coa-subpanel-deferred" },
          { id: "migration",  label: "Migration", testId: "coa-subpanel-migration" },
          { id: "rules",      label: `Rules (${rules.length})`, testId: "coa-subpanel-rules" },
          { id: "activation", label: `Activation (${activationReqs.length})`, testId: "coa-subpanel-activation" },
        ].map(p => (
          <button key={p.id} onClick={() => setSubPanel(p.id)}
            data-testid={p.testId}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors -mb-px border border-b-0 ${
              subPanel === p.id
                ? "bg-white border-[#E5E5E5] text-[#0176D3] font-semibold"
                : "border-transparent text-[#706E6B] hover:text-[#181818]"
            }`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════
          SUB-PANEL: ACCOUNTS LIST
      ══════════════════════════════════════════════════════ */}
      {subPanel === "list" && (
        <div className="space-y-3">
          {/* Search + Filter Bar */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#706E6B]" />
              <input
                data-testid="coa-search"
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search code or name..."
                className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:border-[#0176D3]"
              />
            </div>
            <select data-testid="coa-filter-class" value={filterCls} onChange={e => setFilterCls(e.target.value)}
              className="text-xs px-2 py-1.5 rounded-lg border border-[#E5E5E5] bg-white text-[#444444] focus:outline-none focus:border-[#0176D3]">
              <option value="">All Classes</option>
              {["ASSET","LIABILITY","EQUITY","REVENUE","EXPENSE"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="coa-filter-enabled" value={filterEnabled} onChange={e => setFilterEnabled(e.target.value)}
              className="text-xs px-2 py-1.5 rounded-lg border border-[#E5E5E5] bg-white text-[#444444] focus:outline-none focus:border-[#0176D3]">
              <option value="">All Status</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs text-[#706E6B] cursor-pointer select-none">
              <input type="checkbox" checked={showConflict} onChange={e => setShowConflict(e.target.checked)} className="rounded" />
              <Flag className="w-3 h-3 text-amber-500" /> Conflicts
            </label>
            <label className="flex items-center gap-1.5 text-xs text-[#706E6B] cursor-pointer select-none">
              <input type="checkbox" checked={showDeferred} onChange={e => setShowDeferred(e.target.checked)} className="rounded" />
              <Archive className="w-3 h-3 text-gray-400" /> Deferred
            </label>
            {isDraft && nacctSeg && (
              <button onClick={() => setAddForm(true)}
                data-testid="coa-add-account-btn"
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EEF4FF] border border-[#0176D3]/30 text-[#0176D3] text-xs font-medium hover:bg-[#E5E5E5]">
                <Plus className="w-3.5 h-3.5" /> Add Account
              </button>
            )}
          </div>

          {/* 2-column: Table + Details */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Account Table */}
            <div className={`${selectedVal ? "lg:col-span-2" : "lg:col-span-3"} border border-[#E5E5E5] rounded-xl overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="coa-accounts-table">
                  <thead>
                    <tr className="bg-[#F3F3F3] border-b border-[#E5E5E5] text-xs text-[#706E6B] uppercase font-mono tracking-wider">
                      <th className="px-3 py-2 text-left w-20">Code</th>
                      <th className="px-3 py-2 text-left">Account Name</th>
                      <th className="px-3 py-2 text-left">Class</th>
                      <th className="px-3 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredValues.map(v => (
                      <COAAccountRow key={v.id} val={v} selected={selectedVal?.id === v.id} onSelect={setSelectedVal} />
                    ))}
                  </tbody>
                </table>
                {filteredValues.length === 0 && (
                  <div className="py-12 text-center text-sm text-[#706E6B]">
                    {values.length === 0 ? "No accounts seeded — run migration first" : "No accounts match filters"}
                  </div>
                )}
              </div>
            </div>

            {/* Account Details Panel */}
            {selectedVal && (
              <div className="border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid="coa-account-detail">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-[#F3F3F3] border-b border-[#E5E5E5]">
                  <div>
                    <p className="font-mono font-bold text-[#181818] text-base">{selectedVal.code}</p>
                    <p className="text-xs text-[#706E6B]">Account Details</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isDraft && (
                      <button onClick={() => setEditForm({
                        id: selectedVal.id, name: selectedVal.name,
                        description: selectedVal.description || "",
                        account_subtype: selectedVal.account_subtype || "",
                      })}
                        data-testid="coa-edit-account-btn"
                        className="p-1.5 rounded-lg text-[#0176D3] hover:bg-[#EEF4FF] transition-colors">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => setSelectedVal(null)} className="p-1.5 rounded-lg text-[#706E6B] hover:bg-[#E5E5E5] transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Detail fields */}
                <div className="p-4 space-y-3 text-xs">
                  {[
                    ["Name", selectedVal.name],
                    ["Code", selectedVal.code],
                    ["Classification", <ClassBadge key="cls" cls={selectedVal.account_classification} />],
                    ["Subtype", selectedVal.account_subtype || "—"],
                    ["Normal Balance", selectedVal.normal_balance || "—"],
                    ["Posting Allowed", selectedVal.posting_allowed !== false ? "Yes" : "No"],
                    ["Effective From", selectedVal.effective_from || "—"],
                    ["Effective To", selectedVal.effective_to || "—"],
                  ].map(([label, val]) => (
                    <div key={label} className="flex justify-between gap-2 border-b border-[#F3F3F3] pb-2 last:border-0">
                      <span className="text-[#706E6B] font-mono uppercase tracking-wider flex-shrink-0">{label}</span>
                      <span className="text-[#181818] text-right">{val}</span>
                    </div>
                  ))}

                  {/* Source traceability */}
                  {(selectedVal.workbook_group || selectedVal.source_workbook) && (
                    <div className="pt-2 border-t border-[#E5E5E5]">
                      <p className="text-[#706E6B] font-mono uppercase text-xs tracking-wider mb-2 flex items-center gap-1"><GitBranch className="w-3 h-3" /> Source Traceability</p>
                      {[
                        ["Workbook Group", selectedVal.workbook_group],
                        ["Priority", selectedVal.workbook_priority],
                        ["Source", selectedVal.source_workbook],
                      ].filter(([,v]) => v).map(([l, v]) => (
                        <div key={l} className="flex justify-between text-xs border-b border-[#F3F3F3] pb-1 last:border-0">
                          <span className="text-[#706E6B]">{l}</span>
                          <span className="text-[#444444] font-mono">{v}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Conflict / Deferred flags */}
                  {selectedVal.conflict_flag && (
                    <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <Flag className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-amber-800">Conflict: {selectedVal.conflict_flag}</p>
                        <p className="text-amber-700 mt-0.5">Classified ADD_DISTINCT per Gate 2B</p>
                      </div>
                    </div>
                  )}
                  {selectedVal.defer_flag && (
                    <div className="flex items-start gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
                      <Archive className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-700">Deferred — R2</p>
                        <p className="text-gray-600 mt-0.5">{selectedVal.defer_flag}</p>
                      </div>
                    </div>
                  )}

                  {/* Historical reference protection for v1 ACTIVE */}
                  {isActive && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <Lock className="w-3.5 h-3.5 text-red-600" />
                      <p className="text-xs text-red-700 font-semibold">Protected — ACTIVE version. Read only. Editing disabled.</p>
                    </div>
                  )}

                  {/* Enable / Disable toggle (DRAFT only) */}
                  {isDraft && !selectedVal.defer_flag && (
                    <button
                      data-testid={`coa-toggle-${selectedVal.code}`}
                      onClick={() => handleToggleEnabled(selectedVal)}
                      className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                        selectedVal.enabled
                          ? "bg-white border-red-200 text-red-600 hover:bg-red-50"
                          : "bg-white border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                      }`}
                    >
                      {selectedVal.enabled
                        ? <><ToggleRight className="w-4 h-4" /> Disable Account</>
                        : <><ToggleLeft className="w-4 h-4" /> Enable Account</>
                      }
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          SUB-PANEL: CONFLICT REVIEW
      ══════════════════════════════════════════════════════ */}
      {subPanel === "conflicts" && (
        <div className="space-y-3" data-testid="coa-conflict-panel">
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <Flag className="w-4 h-4 text-amber-600" />
            <p className="text-xs text-amber-800">
              <strong>7 ADD_DISTINCT conflict accounts</strong> — These codes exist in both v1 and v2 with different semantics.
              Gate 2B decision: preserve both independently. No merge or override.
            </p>
          </div>
          {conflictValues.length === 0 ? (
            <p className="text-center py-8 text-sm text-[#706E6B]">No conflict accounts loaded — run migration first</p>
          ) : (
            <div className="border border-amber-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-50 border-b border-amber-200 text-xs text-amber-700 uppercase font-mono tracking-wider">
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Account Name</th>
                    <th className="px-3 py-2 text-left">Class</th>
                    <th className="px-3 py-2 text-left">Conflict Ref</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {conflictValues.map(v => (
                    <tr key={v.id} className="border-b border-amber-100 last:border-0">
                      <td className="px-3 py-2 font-mono font-bold">{v.code}</td>
                      <td className="px-3 py-2">{v.name}</td>
                      <td className="px-3 py-2"><ClassBadge cls={v.account_classification} /></td>
                      <td className="px-3 py-2 font-mono text-amber-700 text-xs">{v.conflict_flag}</td>
                      <td className="px-3 py-2 text-xs text-emerald-700 font-semibold">ADD_DISTINCT</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          SUB-PANEL: DEFERRED ACCOUNTS
      ══════════════════════════════════════════════════════ */}
      {subPanel === "deferred" && (
        <div className="space-y-3" data-testid="coa-deferred-panel">
          <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-xl">
            <Archive className="w-4 h-4 text-gray-500" />
            <p className="text-xs text-gray-700">
              <strong>11 DEFER accounts (R2)</strong> — Deferred to Phase 3 pending Fixed Asset module setup
              and Non-Operating income policy approval. Present as manifest records only — not enabled.
            </p>
          </div>
          {deferredValues.length === 0 ? (
            <p className="text-center py-8 text-sm text-[#706E6B]">No deferred accounts loaded — run migration first</p>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs text-gray-600 uppercase font-mono tracking-wider">
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Account Name</th>
                    <th className="px-3 py-2 text-left">Class</th>
                    <th className="px-3 py-2 text-left">Defer Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {deferredValues.map(v => (
                    <tr key={v.id} className="border-b border-gray-100 last:border-0">
                      <td className="px-3 py-2 font-mono font-bold text-gray-500">{v.code}</td>
                      <td className="px-3 py-2 text-gray-500">{v.name}</td>
                      <td className="px-3 py-2"><ClassBadge cls={v.account_classification} /></td>
                      <td className="px-3 py-2 text-xs text-gray-600">{v.defer_flag}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          SUB-PANEL: MIGRATION (Gate 2C)
      ══════════════════════════════════════════════════════ */}
      {subPanel === "migration" && (
        <div className="space-y-4" data-testid="coa-migration-panel">
          {/* Migration Status */}
          {migStatus && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <COAStatCard label="v2 Seeded" value={migStatus.v2_seeded_complete ? "COMPLETE" : "PENDING"} variant={migStatus.v2_seeded_complete ? "ok" : "warn"} />
              <COAStatCard label="v2 Total" value={migStatus.v2_total_accounts} sub="expected 151" variant="neutral" />
              <COAStatCard label="v1 Preserved" value={migStatus.v1_accounts_preserved} sub="must stay ≥9" variant="ok" />
              <COAStatCard label="Draft Rules" value={migStatus.draft_rules_seeded} sub="expected 20" variant="neutral" />
            </div>
          )}

          <div className="p-4 bg-[#F9F9F9] border border-[#E5E5E5] rounded-xl space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Database className="w-4 h-4 text-[#0176D3]" />
              <h3 className="text-sm font-semibold text-[#181818]">Gate 2C — COA v2 DRAFT Seeding</h3>
            </div>
            <p className="text-xs text-[#706E6B]">
              Stages 151 accounts (140 enabled + 11 deferred manifest records) into a new DRAFT version.
              Safe to re-run — all writes are idempotent. <strong>Does not activate v2 or modify v1.</strong>
            </p>
            <div className="flex gap-2">
              <button
                data-testid="coa-migration-dryrun-btn"
                disabled={migRunning}
                onClick={() => runMigration(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#0176D3] text-[#0176D3] text-sm font-medium hover:bg-[#EEF4FF] transition-colors disabled:opacity-40"
              >
                {migRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                Dry Preview
              </button>
              <button
                data-testid="coa-migration-apply-btn"
                disabled={migRunning}
                onClick={() => {
                  if (window.confirm("Apply COA v2 DRAFT migration? This stages 151 accounts without activating v2. Idempotent — safe to re-run.")) {
                    runMigration(false);
                  }
                }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0176D3] text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
              >
                {migRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                Apply Migration
              </button>
            </div>

            {/* Migration Result */}
            {migResult && (
              <div className="mt-3 space-y-2 border-t border-[#E5E5E5] pt-3" data-testid="coa-migration-result">
                <div className="flex items-center gap-2">
                  {migResult.dry_run ? <Info className="w-4 h-4 text-sky-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                  <p className="text-xs font-semibold text-[#181818]">
                    {migResult.dry_run ? "DRY-RUN PREVIEW" : "MIGRATION APPLIED"} — run at {migResult.run_at}
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs font-mono">
                  {[
                    ["Total", migResult.summary?.total],
                    [migResult.dry_run ? "Would Create" : "Created", migResult.dry_run ? migResult.summary?.would_create : migResult.summary?.created],
                    ["Skipped", migResult.summary?.skipped],
                    ["Enabled", migResult.summary?.enabled_accounts],
                    ["Conflict", migResult.summary?.conflict_accounts],
                    ["Deferred", migResult.summary?.deferred_accounts],
                  ].map(([l, v]) => (
                    <div key={l} className="bg-white border border-[#E5E5E5] rounded p-2 text-center">
                      <p className="text-[#706E6B] text-xs">{l}</p>
                      <p className="font-bold text-[#181818]">{v ?? "—"}</p>
                    </div>
                  ))}
                </div>
                {migResult.reconciliation && (
                  <div className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
                    migResult.reconciliation.v1_preservation_pass && migResult.reconciliation.version_draft_pass
                      ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                      : "bg-red-50 border border-red-200 text-red-700"
                  }`}>
                    {migResult.reconciliation.v1_preservation_pass && migResult.reconciliation.version_draft_pass
                      ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    v1 preserved: {migResult.reconciliation.v1_preservation_pass ? "PASS" : "FAIL"} ·
                    v2 status DRAFT: {migResult.reconciliation.version_draft_pass ? "PASS" : "FAIL"} ·
                    checksum: {migResult.manifest_checksum?.slice(0, 12)}...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Gate 2E — Accounting Rules */}
          <div className="p-4 bg-[#F9F9F9] border border-[#E5E5E5] rounded-xl space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Tag className="w-4 h-4 text-[#0176D3]" />
              <h3 className="text-sm font-semibold text-[#181818]">Gate 2E — Accounting Rule Mappings</h3>
            </div>
            <p className="text-xs text-[#706E6B]">
              Seeds 20 effective-dated AP/AR/Tax/Event mapping rules in DRAFT status (eff. 2026-04-01).
              Validates each target account before insertion. <strong>Requires COA v2 migration to be applied first.</strong>
            </p>
            <div className="flex gap-2">
              <button
                data-testid="coa-rules-dryrun-btn"
                disabled={rulesRunning}
                onClick={() => runRulesMigration(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#0176D3] text-[#0176D3] text-sm font-medium hover:bg-[#EEF4FF] transition-colors disabled:opacity-40"
              >
                {rulesRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                Dry Preview
              </button>
              <button
                data-testid="coa-rules-apply-btn"
                disabled={rulesRunning}
                onClick={() => {
                  if (window.confirm("Seed 20 DRAFT accounting rules referencing v2 accounts?")) {
                    runRulesMigration(false);
                  }
                }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0176D3] text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
              >
                {rulesRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                Seed Rules
              </button>
            </div>

            {rulesResult && (
              <div className="mt-2 border-t border-[#E5E5E5] pt-3 space-y-2">
                <div className="flex items-center gap-2">
                  {rulesResult.dry_run ? <Info className="w-4 h-4 text-sky-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                  <p className="text-xs font-semibold text-[#181818]">
                    {rulesResult.dry_run ? "Rules Dry-Run" : "Rules Applied"} — total:{rulesResult.summary?.total} created:{rulesResult.summary?.created ?? rulesResult.summary?.dry_run_create} skipped:{rulesResult.summary?.skipped} failures:{rulesResult.summary?.validation_failures}
                  </p>
                </div>
                {(rulesResult.results || []).filter(r => r.action === "VALIDATION_FAIL").map(r => (
                  <div key={r.rule_code} className="text-xs p-2 bg-red-50 border border-red-200 rounded text-red-700">
                    {r.rule_code}: {r.reason}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          SUB-PANEL: ACCOUNTING RULES
      ══════════════════════════════════════════════════════ */}
      {subPanel === "rules" && (
        <div className="space-y-3" data-testid="coa-rules-panel">
          <div className="flex items-center gap-2 p-3 bg-sky-50 border border-sky-200 rounded-xl">
            <Tag className="w-4 h-4 text-sky-600" />
            <p className="text-xs text-sky-800">
              <strong>Gate 2E Accounting Rules</strong> — {rules.length} DRAFT rules effective 2026-04-01.
              Not applied to live financial workflows until Finance Owner authorises activation.
            </p>
          </div>
          {rules.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#706E6B]">
              No accounting rules yet. Run "Seed Rules" in the Migration panel.
            </div>
          ) : (
            <div className="border border-[#E5E5E5] rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#F3F3F3] border-b border-[#E5E5E5] text-xs text-[#706E6B] uppercase font-mono tracking-wider">
                    <th className="px-3 py-2 text-left">Rule Code</th>
                    <th className="px-3 py-2 text-left">Transaction Type</th>
                    <th className="px-3 py-2 text-left">Target Account</th>
                    <th className="px-3 py-2 text-left">Eff. From</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map(r => (
                    <tr key={r.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#F9F9F9] transition-colors">
                      <td className="px-3 py-2 font-mono text-[#181818] text-xs">{r.rule_code}</td>
                      <td className="px-3 py-2 text-[#444444]">{r.transaction_type}</td>
                      <td className="px-3 py-2 font-mono">
                        <span className="text-[#0176D3]">{r.result_natural_account_code}</span>
                        <span className="text-[#706E6B] ml-1 text-xs">{r.result_natural_account_name}</span>
                      </td>
                      <td className="px-3 py-2 text-[#706E6B]">{r.effective_from}</td>
                      <td className="px-3 py-2">
                        <span className="bg-amber-50 border border-amber-200 text-amber-700 text-xs px-1.5 py-0.5 rounded font-mono">{r.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          SUB-PANEL: ACTIVATION REQUESTS
      ══════════════════════════════════════════════════════ */}
      {subPanel === "activation" && (
        <div className="space-y-4" data-testid="coa-activation-panel">
          {/* Guard */}
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-red-800">COA v2 Activation — NOT AUTHORISED</p>
              <p className="text-xs text-red-700 mt-0.5">
                Submitting a request creates a PENDING_REVIEW audit record. No activation occurs.
                Finance Owner sign-off and additional authorisation gates are required before v2 goes live.
                COA v1 remains the sole ACTIVE version.
              </p>
            </div>
          </div>

          {/* Request button */}
          {isDraft && !pendingReq && (
            <button
              data-testid="coa-request-activation-btn"
              onClick={() => setActivationModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-amber-300 text-amber-700 text-sm font-medium hover:bg-amber-50 transition-colors w-full justify-center"
            >
              <Send className="w-4 h-4" /> Request Activation Review
            </button>
          )}
          {pendingReq && (
            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-300 rounded-xl">
              <Clock className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Activation Request PENDING REVIEW</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Submitted by {pendingReq.requested_by_email} on {pendingReq.requested_at?.slice(0, 10)} ·
                  Proposed effective: {pendingReq.requested_effective_date}
                </p>
              </div>
            </div>
          )}

          {/* History */}
          {activationReqs.length > 0 && (
            <div className="border border-[#E5E5E5] rounded-xl overflow-hidden">
              <div className="px-4 py-2 bg-[#F3F3F3] border-b border-[#E5E5E5] text-xs font-mono uppercase tracking-wider text-[#706E6B]">Activation Request History</div>
              <div className="divide-y divide-[#E5E5E5]">
                {activationReqs.map(r => (
                  <div key={r.id} className="p-4 text-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-0.5 rounded font-mono font-semibold text-xs ${
                        r.status === "PENDING_REVIEW" ? "bg-amber-100 text-amber-700" :
                        r.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" :
                        "bg-gray-100 text-gray-600"
                      }`}>{r.status}</span>
                      <span className="text-[#706E6B]">{r.requested_at?.slice(0, 10)}</span>
                    </div>
                    <p className="text-[#444444]"><strong>By:</strong> {r.requested_by_email}</p>
                    <p className="text-[#444444]"><strong>Proposed effective:</strong> {r.requested_effective_date}</p>
                    <p className="text-[#706E6B] italic">{r.justification}</p>
                    {r.review_note && <p className="text-[#444444]"><strong>Review note:</strong> {r.review_note}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {activationReqs.length === 0 && (
            <p className="text-center py-6 text-sm text-[#706E6B]">No activation requests submitted for this version.</p>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════════════════ */}

      {/* Add Account Modal */}
      {addForm && (
        <Modal title="Add Account — DRAFT v2" onClose={() => setAddForm(false)}>
          <form onSubmit={handleAddAccount} className="space-y-3">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
              Adding an account to a DRAFT version only. It will not appear in active financial workflows until v2 is authorised.
            </div>
            {[
              ["code", "Account Code *", "e.g. 1050 or 101010", true, "text"],
              ["name", "Account Name *", "e.g. Petty Cash", true, "text"],
              ["account_subtype", "Subtype", "e.g. CASH", false, "text"],
              ["effective_from", "Effective From *", "YYYY-MM-DD", true, "date"],
            ].map(([field, label, placeholder, required, type]) => (
              <div key={field}>
                <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{label}</label>
                <input type={type} required={required} placeholder={placeholder}
                  value={addAccountForm[field]} onChange={e => setAddAccountForm(p => ({ ...p, [field]: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:border-[#0176D3]" />
              </div>
            ))}
            <div>
              <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">Classification *</label>
              <select required value={addAccountForm.account_classification}
                onChange={e => setAddAccountForm(p => ({ ...p, account_classification: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:border-[#0176D3]">
                {["ASSET","LIABILITY","EQUITY","REVENUE","EXPENSE"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <SubmitBtn loading={false} label="Add to DRAFT" />
          </form>
        </Modal>
      )}

      {/* Edit Account Modal */}
      {editForm && (
        <Modal title={`Edit Account — ${editForm.id}`} onClose={() => setEditForm(null)}>
          <form onSubmit={handleEditSubmit} className="space-y-3">
            <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-800">
              Permitted edits: name, description, subtype. Account code and classification are protected.
            </div>
            {[
              ["name", "Account Name *", true],
              ["description", "Description", false],
              ["account_subtype", "Subtype", false],
            ].map(([field, label, required]) => (
              <div key={field}>
                <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{label}</label>
                <input type="text" required={required}
                  value={editForm[field] || ""} onChange={e => setEditForm(p => ({ ...p, [field]: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:border-[#0176D3]" />
              </div>
            ))}
            <SubmitBtn loading={false} label="Save Changes" />
          </form>
        </Modal>
      )}

      {/* Request Activation Modal */}
      {activationModal && (
        <Modal title="Request v2 Activation — PENDING REVIEW" onClose={() => setActivationModal(false)}>
          <form onSubmit={handleRequestActivation} className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
              <strong>This submits an activation request only.</strong> COA v2 will NOT be activated. Finance Owner review and authorisation are required before any activation occurs.
            </div>
            <div>
              <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">Justification *</label>
              <textarea required rows={4} value={activationForm.justification}
                onChange={e => setActivationForm(p => ({ ...p, justification: e.target.value }))}
                placeholder="Describe the business reason for requesting activation..."
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:border-[#0176D3] resize-none" />
            </div>
            <div>
              <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">Proposed Effective Date *</label>
              <input type="date" required value={activationForm.requested_effective_date}
                onChange={e => setActivationForm(p => ({ ...p, requested_effective_date: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:border-[#0176D3]" />
            </div>
            <div className="space-y-2">
              <p className="text-xs font-mono text-[#706E6B] uppercase tracking-widest">Acknowledgements</p>
              {[
                "I confirm 140 candidate accounts are reviewed and approved.",
                "I confirm 11 deferred accounts require separate Phase 3 authorisation.",
                "I confirm 7 conflict accounts are classified ADD_DISTINCT per Gate 2B.",
                "I confirm COA v1 ACTIVE records are preserved and unmodified.",
              ].map(ack => (
                <label key={ack} className="flex items-start gap-2 text-xs text-[#444444] cursor-pointer">
                  <input type="checkbox"
                    checked={activationForm.acknowledgements.includes(ack)}
                    onChange={e => setActivationForm(p => ({
                      ...p, acknowledgements: e.target.checked
                        ? [...p.acknowledgements, ack]
                        : p.acknowledgements.filter(a => a !== ack)
                    }))}
                    className="mt-0.5 rounded flex-shrink-0"
                  />
                  {ack}
                </label>
              ))}
            </div>
            <SubmitBtn loading={false} label="Submit Activation Request" />
          </form>
        </Modal>
      )}
    </div>
  );
}


// ── ERP-FIN-02 AR-05: Accounting Readiness Tab Component ─────────────────────
function AR05ReadinessTab({ data, loading, onRefresh }) {
  if (loading) {
    return (
      <div className="text-center py-12 text-[#706E6B]">
        <div className="w-6 h-6 border-2 border-[#0176D3] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        Loading accounting readiness…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="text-center py-12 text-[#706E6B]">
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-[#0176D3] text-white rounded-lg text-sm font-medium mx-auto"
        >
          <RefreshCw size={14} /> Load Readiness Report
        </button>
      </div>
    );
  }

  const d = data;
  // API returns: readiness_status, coa_status, ar_rules, missing_mappings, warnings
  const overall = d.readiness_status || "UNKNOWN";
  const statusColor = overall === "READY" ? "#166534" : overall === "PARTIAL" ? "#D97706" : "#DC2626";
  const statusBg   = overall === "READY" ? "#F0FDF4" : overall === "PARTIAL" ? "#FFFBEB" : "#FEF2F2";

  const coa   = d.coa_status   || {};
  const rules = d.ar_rules     || {};
  const gaps  = d.missing_mappings || [];
  const warns = d.warnings     || [];

  return (
    <div data-testid="ar05-readiness-tab" className="space-y-6">
      {/* Header banner */}
      <div className="flex items-center justify-between">
        <div
          style={{ background: statusBg, borderColor: statusColor + "44", color: statusColor }}
          className="flex items-center gap-3 px-5 py-3 rounded-xl border font-semibold text-sm"
        >
          {overall === "READY"
            ? <CheckCircle size={18} />
            : <AlertTriangle size={18} />
          }
          <span>AR-05 Accounting Readiness: {overall}</span>
          <span className="text-xs font-mono opacity-70">Audited: {d.audit_date || "—"}</span>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-3 py-2 text-xs border border-[#E5E5E5] rounded-lg hover:bg-[#F3F3F3]"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* COA version status */}
      <div className="bg-[#F7F9FC] border border-[#E5E5E5] rounded-xl p-5">
        <h3 className="text-sm font-bold text-[#181818] mb-4 flex items-center gap-2">
          <GitBranch size={15} /> COA Version Status
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-[#E5E5E5] rounded-lg p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-sm">COA v1</span>
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, fontWeight: 700, background: "#F0FDF4", color: "#166534" }}>
                {coa.v1_active ? "ACTIVE" : "INACTIVE"}
              </span>
            </div>
            <div className="text-xs text-[#706E6B]">ID: {coa.v1_id || "—"}</div>
            {coa.v1_name && <div className="text-xs text-[#706E6B]">{coa.v1_name}</div>}
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-lg p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-sm">COA v2</span>
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, fontWeight: 700, background: "#FFF7ED", color: "#92400E" }}>
                {coa.v2_draft ? "DRAFT" : "UNKNOWN"}
              </span>
            </div>
            <div className="text-xs text-[#706E6B]">ID: {coa.v2_id || "—"}</div>
            {coa.activation_blocked && (
              <div className="text-xs text-[#D97706] mt-1 font-medium">
                COA v2 activation blocked — {coa.restriction || "per ARGOV MASTER-01"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rule Coverage */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
          <BookOpen size={15} className="text-[#0176D3]" />
          <h3 className="text-sm font-bold text-[#181818]">AR Accounting Rule Coverage</h3>
          <span className="ml-auto text-xs text-[#706E6B]">{rules.total_rules ?? 0} rules in collection</span>
        </div>
        <div className="grid grid-cols-3 gap-0 border-b border-[#E5E5E5]">
          {[
            { label: "Total Rules", value: rules.total_rules ?? 0, color: "#0176D3" },
            { label: "DRAFT (not active)", value: rules.draft_rules ?? 0, color: "#D97706" },
            { label: "Active Rules", value: rules.active_rules ?? 0, color: "#166534" },
          ].map(({ label, value, color }) => (
            <div key={label} className="px-5 py-4 text-center border-r border-[#E5E5E5] last:border-r-0">
              <div className="text-2xl font-bold" style={{ color }}>{value}</div>
              <div className="text-xs text-[#706E6B] mt-1">{label}</div>
            </div>
          ))}
        </div>
        <div className="px-5 py-3">
          <div className="text-xs text-[#706E6B] font-mono">
            Rule codes: {(rules.rule_codes || []).slice(0, 8).join(", ")}{(rules.rule_codes?.length ?? 0) > 8 ? ` +${rules.rule_codes.length - 8} more` : ""}
          </div>
        </div>
      </div>

      {/* Missing Mappings */}
      {gaps.length > 0 && (
        <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-xl p-5">
          <h3 className="text-sm font-bold text-[#92400E] mb-3 flex items-center gap-2">
            <AlertTriangle size={15} /> Missing AR-Event Mappings ({gaps.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/60">
                <tr>
                  {["Transaction Type", "Dr Account", "Cr Account", "Severity", "Note"].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-semibold text-[#374151] border-b border-[#FDE68A]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gaps.map((g, i) => (
                  <tr key={i} data-testid={`gap-row-${i}`} className="border-b border-[#FEF9C3]">
                    <td className="px-3 py-2 font-mono font-semibold text-[#92400E]">{g.tx_type}</td>
                    <td className="px-3 py-2 text-[#374151]">{g.required_dr || "—"}</td>
                    <td className="px-3 py-2 text-[#374151]">{g.required_cr || "—"}</td>
                    <td className="px-3 py-2">
                      <span style={{
                        padding: "1px 7px", borderRadius: 10, fontSize: 10, fontWeight: 700,
                        background: g.severity === "HIGH" ? "#FEF2F2" : "#FFFBEB",
                        color:      g.severity === "HIGH" ? "#DC2626" : "#92400E",
                      }}>{g.severity || "—"}</span>
                    </td>
                    <td className="px-3 py-2 text-[#706E6B]">{g.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Warnings */}
      {warns.length > 0 && (
        <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-xl p-4">
          <h3 className="text-sm font-bold text-[#DC2626] mb-2 flex items-center gap-2">
            <XCircle size={14} /> Warnings ({warns.length})
          </h3>
          <ul className="space-y-1">
            {warns.map((w, i) => (
              <li key={i} className="text-xs text-[#991B1B] flex items-start gap-2">
                <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer note */}
      <div className="text-xs text-[#706E6B] bg-[#F9FAFB] rounded-xl p-4 border border-[#E5E5E5]">
        <strong>AR-05 Read-only audit.</strong> This view reflects the current state of accounting rule mappings.
        COA v1 is ACTIVE. COA v2 is DRAFT. No rules will be published or activated from this view.
        No GL posting is performed. No bank transfers are triggered.
        <br/>
        <span className="text-[#D97706]">Note: {rules.active_rules ?? 0} active rules — {rules.total_rules ?? 0} rules remain in DRAFT awaiting COA v2 authorization.</span>
      </div>
    </div>
  );
}
